/**
 * Single WebSocket multiplexer (platform plan §4.1) — one connection, refcounted
 * CEX /api/ws topics. Migrated from ad-hoc hooks incrementally.
 */
const RECONNECT_BASE_MS = 800
const RECONNECT_MAX_MS = 30_000
/** Don't flip the UI to Offline until disconnected this long (sliding window on each close) — absorbs reconnect + backoff */
const CONNECTION_OFFLINE_GRACE_MS = 4000
const CLOSE_POLICY_VIOLATION = 1008

function _wsRecovering(ws) {
  const r = ws?.readyState
  return r === WebSocket.OPEN || r === WebSocket.CONNECTING
}

function _marketKey(accountId, exchange, symbol) {
  return `mkt:${String(accountId || '')}\0${String(exchange || '').toLowerCase()}\0${String(symbol || '')}`
}

function _dashboardKey(groupId, period, mode) {
  return `dash:${String(groupId || '')}\0${String(period || '7d')}\0${String(mode || 'rolling')}`
}

class WsHub {
  constructor() {
    this._url = null
    this._ws = null
    this._ref = new Map() // subject -> count
    this._listeners = new Map() // subject -> Set<fn>
    /** @type {Map<string, { n: number, accountId: string, exchange: string, symbol: string }>} CEX `subscribeMarket` ref-counts */
    this._market = new Map()
    /** @type {Map<string, { n: number, groupId: string, period: string, mode: string }>} CEX dashboard stream ref-counts */
    this._dashboard = new Map()
    this._reconnectT = null
    this._backoff = RECONNECT_BASE_MS
    /** @type {number} last JetStream `streamSeq` seen on an `exec..fill` frame (for optional WS resume). */
    this._lastExecFillStreamSeq = 0
    /** @type {ReturnType<typeof setTimeout> | null} delayed Offline so brief closes do not flicker */
    this._offlineGraceTimer = null
    /** @type {Set<(open: boolean) => void>} */
    this._statusListeners = new Set()
    /** @type {Set<(msg: object) => void>}  receive every message (no server-side subscribe) */
    this._allMsgListeners = new Set()
  }

  _notifyConnection(open) {
    for (const fn of this._statusListeners) {
      try {
        fn(open)
      } catch {
        /* ignore */
      }
    }
  }

  /** Report socket up/down for UI badges; Offline is deferred to avoid Live/Offline flashing on reconnect */
  _emitStatus(open) {
    if (open) {
      if (this._offlineGraceTimer != null) {
        clearTimeout(this._offlineGraceTimer)
        this._offlineGraceTimer = null
      }
      this._notifyConnection(true)
      return
    }
    if (this._offlineGraceTimer != null) {
      clearTimeout(this._offlineGraceTimer)
      this._offlineGraceTimer = null
    }
    this._offlineGraceTimer = setTimeout(() => {
      this._offlineGraceTimer = null
      if (_wsRecovering(this._ws)) return
      this._notifyConnection(false)
    }, CONNECTION_OFFLINE_GRACE_MS)
  }

  /**
   * @param {(open: boolean) => void} fn
   * @returns {() => void} unsubscribe
   */
  subscribeConnection(fn) {
    this._statusListeners.add(fn)
    const alive = !!(this._ws && _wsRecovering(this._ws))
    try {
      fn(alive)
    } catch {
      /* ignore */
    }
    return () => {
      this._statusListeners.delete(fn)
    }
  }

  /**
   * @param {string} url  e.g. wss://cex.../api/ws?token= or from cexWebSocketUrl()
   */
  connect(url) {
    this._url = url
    if (
      this._ws &&
      (this._ws.readyState === WebSocket.OPEN || this._ws.readyState === WebSocket.CONNECTING)
    ) {
      return
    }
    this._open()
  }

  _open() {
    if (!this._url) return
    let ws
    try {
      ws = new WebSocket(this._url)
      this._ws = ws
    } catch {
      this._scheduleReconnect()
      return
    }
    ws.onmessage = (ev) => {
      if (this._ws !== ws) return
      let data = ev.data
      try {
        data = JSON.parse(String(data))
      } catch {
        return
      }
      for (const fn of this._allMsgListeners) {
        try {
          fn(data)
        } catch {
          /* ignore */
        }
      }
      const ss = data?.streamSeq
      if (typeof ss === 'number' && ss > 0) {
        if (ss > this._lastExecFillStreamSeq) {
          this._lastExecFillStreamSeq = ss
        }
      }
      const subj = data?.subject || data?.s
      if (subj && this._listeners.has(subj)) {
        for (const fn of this._listeners.get(subj) || []) {
          try {
            fn(data)
          } catch {
            /* ignore */
          }
        }
      }
    }
    ws.onopen = () => {
      if (this._ws !== ws || ws.readyState !== WebSocket.OPEN) return
      this._backoff = RECONNECT_BASE_MS
      this._emitStatus(true)
      if (this._lastExecFillStreamSeq > 0) {
        try {
          ws.send(
            JSON.stringify({
              type: 'resume',
              lastStreamSeq: this._lastExecFillStreamSeq,
            }),
          )
        } catch {
          /* ignore */
        }
      }
      for (const subj of this._ref.keys()) {
        ws.send(JSON.stringify({ type: 'subscribe', subjects: [subj] }))
      }
      for (const { n, accountId, exchange, symbol } of this._market.values()) {
        if (n > 0) {
          ws.send(
            JSON.stringify({ type: 'subscribeMarket', accountId, exchange, symbol }),
          )
        }
      }
      for (const { n, groupId, period, mode } of this._dashboard.values()) {
        if (n > 0) {
          ws.send(JSON.stringify({ type: 'subscribeDashboard', groupId, period, mode }))
        }
      }
    }
    ws.onclose = (ev) => {
      if (this._ws !== ws) return
      this._emitStatus(false)
      if (ev?.code === CLOSE_POLICY_VIOLATION) {
        this._ws = null
        return
      }
      this._scheduleReconnect()
    }
    ws.onerror = () => {
      try {
        ws.close()
      } catch {
        /* ignore */
      }
    }
  }

  _scheduleReconnect() {
    if (this._reconnectT) return
    this._reconnectT = setTimeout(() => {
      this._reconnectT = null
      if (!this._url) return
      this._open()
      this._backoff = Math.min(RECONNECT_MAX_MS, Math.floor(this._backoff * 1.5))
    }, this._backoff)
  }

  /**
   * @param {(msg: object) => void} fn
   * @returns {() => void}
   */
  subscribeAllMessages(fn) {
    this._allMsgListeners.add(fn)
    if (this._url && !this._ws) this._open()
    return () => {
      this._allMsgListeners.delete(fn)
    }
  }

  /**
   * @param {string} subject  NATS-style subject
   * @param {(msg: object) => void} fn
   */
  subscribe(subject, fn) {
    const c = (this._ref.get(subject) || 0) + 1
    this._ref.set(subject, c)
    if (!this._listeners.has(subject)) this._listeners.set(subject, new Set())
    this._listeners.get(subject).add(fn)
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({ type: 'subscribe', subjects: [subject] }))
    } else {
      this.connect(this._url)
    }
    return () => this.unsubscribe(subject, fn)
  }

  /**
   * CEX market stream: server only starts NATS forwarders after `subscribeMarket`.
   * Ref-count so one WebSocket and one publisher serve multiple terminal panels.
   * @returns {() => void} release
   */
  acquireMarketStream(accountId, exchange, symbol) {
    const k = _marketKey(accountId, exchange, symbol)
    let cur = this._market.get(k)
    if (!cur) {
      cur = { n: 0, accountId, exchange: String(exchange || '').toLowerCase(), symbol: String(symbol) }
    }
    cur.n += 1
    this._market.set(k, cur)
    if (cur.n === 1) {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(
          JSON.stringify({ type: 'subscribeMarket', accountId, exchange: cur.exchange, symbol: cur.symbol }),
        )
      } else {
        this.connect(this._url)
      }
    }
    return () => this.releaseMarketStream(accountId, exchange, symbol)
  }

  releaseMarketStream(accountId, exchange, symbol) {
    const k = _marketKey(accountId, exchange, symbol)
    const cur = this._market.get(k)
    if (!cur) return
    cur.n -= 1
    if (cur.n <= 0) {
      this._market.delete(k)
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(
          JSON.stringify({ type: 'unsubscribeMarket', accountId, exchange: cur.exchange, symbol: cur.symbol }),
        )
      }
    } else {
      this._market.set(k, cur)
    }
  }

  acquireDashboardStream(groupId, period = '7d', mode = 'rolling') {
    const k = _dashboardKey(groupId, period, mode)
    let cur = this._dashboard.get(k)
    if (!cur) {
      cur = { n: 0, groupId: String(groupId || ''), period: String(period || '7d'), mode: String(mode || 'rolling') }
    }
    cur.n += 1
    this._dashboard.set(k, cur)
    if (cur.n === 1) {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify({ type: 'subscribeDashboard', groupId: cur.groupId, period: cur.period, mode: cur.mode }))
      } else {
        this.connect(this._url)
      }
    }
    return () => this.releaseDashboardStream(groupId, period, mode)
  }

  releaseDashboardStream(groupId, period = '7d', mode = 'rolling') {
    const k = _dashboardKey(groupId, period, mode)
    const cur = this._dashboard.get(k)
    if (!cur) return
    cur.n -= 1
    if (cur.n <= 0) {
      this._dashboard.delete(k)
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(
          JSON.stringify({
            type: 'unsubscribeDashboard',
            groupId: cur.groupId,
            period: cur.period,
            mode: cur.mode,
          }),
        )
      }
    } else {
      this._dashboard.set(k, cur)
    }
  }

  /**
   * Local listener for a `subject` without sending `subscribe` (used with `acquireMarketStream`, which starts server-side forwards).
   * @param {string} subject e.g. md.binance.BTCUSDT.book
   * @param {(msg: object) => void} fn receives full frame `{ subject, data }`
   * @returns {() => void}
   */
  listenOnly(subject, fn) {
    if (!this._listeners.has(subject)) this._listeners.set(subject, new Set())
    this._listeners.get(subject).add(fn)
    return () => {
      const s = this._listeners.get(subject)
      if (s) {
        s.delete(fn)
        if (s.size === 0) this._listeners.delete(subject)
      }
    }
  }

  unsubscribe(subject, fn) {
    const s = this._listeners.get(subject)
    if (s) {
      s.delete(fn)
      if (s.size === 0) this._listeners.delete(subject)
    }
    const c = (this._ref.get(subject) || 1) - 1
    if (c <= 0) {
      this._ref.delete(subject)
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify({ type: 'unsubscribe', subjects: [subject] }))
      }
    } else {
      this._ref.set(subject, c)
    }
  }

  close() {
    if (this._offlineGraceTimer != null) {
      clearTimeout(this._offlineGraceTimer)
      this._offlineGraceTimer = null
    }
    this._ref.clear()
    this._listeners.clear()
    this._allMsgListeners.clear()
    this._market.clear()
    this._dashboard.clear()
    if (this._reconnectT) {
      clearTimeout(this._reconnectT)
      this._reconnectT = null
    }
    try {
      this._ws?.close()
    } catch {
      /* ignore */
    }
    this._ws = null
    this._url = null
    this._notifyConnection(false)
  }
}

let _singleton
export function getWsHub() {
  if (!_singleton) _singleton = new WsHub()
  return _singleton
}
